/**
 * Requirements Badge Hook
 * Integration Phase: Silent requirements checking with visual badges
 * Shows "Requirements updates available" without users hunting in Settings
 */
import { useState, useEffect } from 'react';
// Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
// import { useVesselCollections } from './useVesselCollections';
// import { evaluateAllRequirements } from '../req/evaluator.v2'; // TEMP DISABLED

/**
 * Hook for requirements badge functionality
 * @param {string} moduleType - Module type ('equipment', 'procedures', 'drills', 'training', 'inspections')
 * @param {Object} vesselConfig - Current vessel configuration
 * @returns {Object} Badge state and actions
 */
export function useRequirementsBadge(moduleType, vesselConfig) {
  // Phase 2.6: Collections disabled - will restore in Phase 3
  // const { collections, vesselId } = useVesselCollections();
  const [hasUpdates, setHasUpdates] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const [checking, setChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  // Phase 2.6: Requirements checking temporarily disabled - will restore in Phase 3
  // Silent evaluation check
  const checkForUpdates = async (silent = true) => {
    // Phase 2.6: Collections disabled
    console.warn('Phase 2.6: Requirements checking disabled - will return in Phase 3');
    setChecking(false);
    return;

    // if (!vesselConfig || checking || !collections) return;
    //
    // setChecking(true);
    // try {
    //   // TEMPORARILY DISABLED - Causing infinite console flooding
    //   // const evaluation = await evaluateAllRequirements(collections, vesselConfig);
    //   const evaluation = { upToDate: true, changes: [] }; // Mock response
    //
    //   if (evaluation.upToDate) {
    //     setHasUpdates(false);
    //     setUpdateCount(0);
    //   } else {
    //     // Filter changes for this module type
    //     const moduleChanges = evaluation.changes?.filter(change =>
    //       change.collection === moduleType
    //     ) || [];
    //
    //     setHasUpdates(moduleChanges.length > 0);
    //     setUpdateCount(moduleChanges.length);
    //   }
    //
    //   setLastCheck(new Date());
    //
    //   if (!silent) {
    //
    //   }
    // } catch (error) {
    //
    //   setHasUpdates(false);
    //   setUpdateCount(0);
    // } finally {
    //   setChecking(false);
    // }
  };

  // Phase 2.6: Auto-check temporarily disabled - will restore in Phase 3
  // Auto-check on mount and vessel config changes
  useEffect(() => {
    // Phase 2.6: Collections disabled - no checks
  }, [vesselConfig?.vesselName, vesselConfig?.vesselType, vesselConfig?.category, moduleType]); // Use stable properties to prevent render loops

  // Manual refresh
  const refreshBadge = () => {
    checkForUpdates(false);
  };

  // Clear badge after requirements sync
  const clearBadge = () => {
    setHasUpdates(false);
    setUpdateCount(0);
    setLastCheck(new Date());
  };

  return {
    hasUpdates,
    updateCount,
    checking,
    lastCheck,
    refreshBadge,
    clearBadge,
    checkForUpdates: () => checkForUpdates(false)
  };
}

/**
 * Requirements Badge Component
 * @param {Object} props - Component props
 * @param {boolean} props.hasUpdates - Whether updates are available
 * @param {number} props.updateCount - Number of updates
 * @param {boolean} props.checking - Whether currently checking
 * @param {Function} props.onSync - Callback when sync button clicked
 * @returns {JSX.Element} Badge component
 */
export function RequirementsBadge({ hasUpdates, updateCount, checking, onSync }) {
  if (checking) {
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Checking...
      </div>
    );
  }

  if (!hasUpdates) {
    return null;
  }

  return (
    <div className="inline-flex items-center space-x-2">
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {updateCount} requirement{updateCount !== 1 ? 's' : ''} pending
      </span>
      
      <button
        onClick={onSync}
        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Sync Requirements
      </button>
    </div>
  );
}

/**
 * Full Requirements Sync Button with Badge
 * Combined component for easy integration into module headers
 */
export function RequirementsSyncButton({ moduleType, vesselConfig, onSyncComplete }) {
  const badge = useRequirementsBadge(moduleType, vesselConfig);

  // Phase 2.6: Sync functionality temporarily disabled - will restore in Phase 3
  const handleSync = async () => {
    console.warn('Phase 2.6: Requirements sync disabled - will return in Phase 3');
    return;

    // try {
    //   // Import the requirements diff panel functionality
    //   const { evaluateAllRequirements, applyAllChanges } = await import('../req/evaluator.v2');
    //   const { collections } = useVesselCollections();
    //
    //   // Evaluate and apply changes
    //   const evaluation = await evaluateAllRequirements(collections, vesselConfig);
    //
    //   if (!evaluation.upToDate && evaluation.changes?.length > 0) {
    //     // Filter for this module
    //     const moduleChanges = evaluation.changes.filter(change =>
    //       change.collection === moduleType
    //     );
    //
    //     if (moduleChanges.length > 0) {
    //       await applyAllChanges(collections, moduleChanges);
    //       badge.clearBadge();
    //
    //       if (onSyncComplete) {
    //         onSyncComplete(moduleChanges.length);
    //       }
    //     }
    //   }
    // } catch (error) {
    //
    // }
  };

  return (
    <RequirementsBadge
      hasUpdates={badge.hasUpdates}
      updateCount={badge.updateCount}
      checking={badge.checking}
      onSync={handleSync}
    />
  );
}

/**
 * Utility: Get collection name from module type
 * @param {string} moduleType - Module type
 * @returns {string} Collection name
 */
export function getCollectionFromModuleType(moduleType) {
  const mapping = {
    equipment: 'equipment',
    procedures: 'procedures', 
    drills: 'drills',
    training: 'training',
    inspections: 'inspections',
    maintenance: 'maintenance'
  };
  
  return mapping[moduleType] || moduleType;
}

/**
 * Utility: Get module display name
 * @param {string} moduleType - Module type
 * @returns {string} Display name
 */
export function getModuleDisplayName(moduleType) {
  const mapping = {
    equipment: 'Equipment',
    procedures: 'Procedures',
    drills: 'Drills', 
    training: 'Training',
    inspections: 'Inspections',
    maintenance: 'Maintenance'
  };
  
  return mapping[moduleType] || moduleType;
}